import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import ProTable, { ActionType } from 'src/app/common/components/ProTable';
import { getChangeRequestHistoryList } from '../../../../network/personalParticularCrud';
import { formatAmount, formatDate } from './document.utils';
import {
  ChangeRequestHistoryItem,
  ChangeRequestSectionEnum,
} from 'src/app/modules/Recruit/types/personal-particular-types';
import { useDispatch } from 'react-redux';

import { forwardRef, useImperativeHandle } from 'react';
import { Button, Dialog } from '@mui/material';

type Props = {
  appId: string;
  popUpVisible: boolean;
  setPopUpVisible: (visible: boolean) => void;
};

export type ChangeRequestsHistoryrRef = {
  reloadData: () => void;
};

export const ChangeRequestsHistoryDialog = forwardRef<ChangeRequestsHistoryrRef, Props>(
  ({ appId, popUpVisible, setPopUpVisible }, ref) => {
    const intl = useIntl();
    const t = (id: string) => intl.formatMessage({ id });
    const dispatch = useDispatch();
    const actionRef = useRef<ActionType>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [data, setData] = useState<ChangeRequestHistoryItem[] | null>(null);

    const reloadData = async () => {
      if (!appId) return;
      try {
        setIsLoading(true);
        setData(null);
        const result = await getChangeRequestHistoryList(appId, dispatch);

        const sortedData = result?.result?.sort((a, b) => {
          if (!a?.actionDate) return 1;
          if (!b?.actionDate) return -1;
          return new Date(b.actionDate).getTime() - new Date(a.actionDate).getTime();
        });
        setData(sortedData);
      } catch (err) {
        console.log(err);
      } finally {
        setIsLoading(false);
      }
    };

    useEffect(() => {
      if (popUpVisible && appId && !data && !isLoading) {
        reloadData();
      }
    }, [popUpVisible, appId]);

    useImperativeHandle(ref, () => ({
      reloadData,
    }));

    useEffect(() => {
      if (data) {
        actionRef?.current?.reload();
      }
    }, [data]);

    const formatData = async (newFilterState?: any) => {
      let sourceData = data || [];
      if (!data) {
        return {
          data: [],
          success: true,
          total: 0,
        };
      }

      const { page, pageSize, section } = newFilterState;
      const filteredData = section ? sourceData.filter((item) => item.section === section) : sourceData;

      const list = filteredData.slice(page * pageSize, page * pageSize + pageSize);

      const res = {
        data: list || [],
        success: true,
        total: filteredData.length,
      };

      return res;
    };

    const columns = useMemo(
      () => [
        {
          title: t('recruit.personalParticular.crHistory.changeItem'),
          dataIndex: 'section',
          valueEnum: {
            '': { text: '' },
            ...Object.fromEntries(
              Object.values(ChangeRequestSectionEnum).map((item) => [
                item,
                {
                  text: t(`recruit.personalParticular.crHistory.changeItem.options.${item}`),
                },
              ]),
            ),
          },
          render: (_: any, data: any) => {
            return t(`recruit.personalParticular.crHistory.changeItem.options.${data.section}`);
          },
        },
        {
          title: t('recruit.personalParticular.crHistory.previousValue'),
          dataIndex: 'previousValue',
          hideInSearch: true,
          render: (_: any, data: any) => {
            switch (data.section) {
              case ChangeRequestSectionEnum.MOBILE:
                return data?.previousValue?.mobilePhone || '-';
              case ChangeRequestSectionEnum.EMAIL:
                return data?.previousValue?.email || '-';
              case ChangeRequestSectionEnum.DESIGNATION:
                return data?.previousValue?.designation ? t(data?.previousValue?.designation) : '-';
              case ChangeRequestSectionEnum.FINANCING_PACKAGE:
                return data?.previousValue?.finCategory ? t(data?.previousValue?.finCategory) : '-';
              default:
                return '-';
            }
          },
        },
        {
          title: t('recruit.personalParticular.crHistory.newValue'),
          dataIndex: 'newValue',
          hideInSearch: true,
          render: (_: any, data: any) => {
            switch (data.section) {
              case ChangeRequestSectionEnum.MOBILE:
                return data?.newValue?.mobilePhone || '-';
              case ChangeRequestSectionEnum.EMAIL:
                return data?.newValue?.email || '-';
              case ChangeRequestSectionEnum.DESIGNATION:
                return data?.newValue?.designation ? t(data?.newValue?.designation) : 'N/A';
              case ChangeRequestSectionEnum.FINANCING_PACKAGE:
                return data?.newValue?.finCategory ? t(data?.newValue?.finCategory) : 'N/A';
              default:
                return '-';
            }
          },
        },
        {
          title: t('recruit.personalParticular.crHistory.previousFinancingScheme'),
          dataIndex: 'previousFinancingScheme',
          hideInSearch: true,
          render: (_: any, data: any) =>
            data.section === ChangeRequestSectionEnum.FINANCING_PACKAGE && data?.previousValue?.finScheme
              ? t(data.previousValue.finScheme)
              : 'N/A',
        },
        {
          title: t('recruit.personalParticular.crHistory.newFinancingScheme'),
          dataIndex: 'newFinancingScheme',
          hideInSearch: true,
          render: (_: any, data: any) =>
            data.section === ChangeRequestSectionEnum.FINANCING_PACKAGE && data?.newValue?.finScheme
              ? t(data.newValue.finScheme)
              : 'N/A',
        },
        {
          title: t('recruit.personalParticular.crHistory.previousMonthlyFinanceAmount'),
          dataIndex: 'previousMonthlyFinanceAmount',
          hideInSearch: true,
          render: (_: any, data: any) =>
            data.section === ChangeRequestSectionEnum.FINANCING_PACKAGE && data?.previousValue?.finApplyMonthly
              ? '$' + formatAmount(data.previousValue.finApplyMonthly)
              : 'N/A',
        },
        {
          title: t('recruit.personalParticular.crHistory.newMonthlyFinanceAmount'),
          dataIndex: 'newMonthlyFinanceAmount',
          hideInSearch: true,
          render: (_: any, data: any) =>
            data.section === ChangeRequestSectionEnum.FINANCING_PACKAGE && data?.newValue?.finApplyMonthly
              ? '$' + formatAmount(data.newValue.finApplyMonthly)
              : 'N/A',
        },
        {
          title: t('recruit.personalParticular.crHistory.recommendationReason'),
          dataIndex: 'recommendationReason',
          hideInSearch: true,
          render: (_: any, data: any) =>
            data.section === ChangeRequestSectionEnum.DESIGNATION && data?.newValue?.recommendReason
              ? data.newValue.recommendReason
              : 'N/A',
        },
        {
          title: t('recruit.personalParticular.crHistory.changeRequestSubmissionDateTime'),
          dataIndex: 'submittedDate',
          hideInSearch: true,
          render: (value: any) => <span>{formatDate(value)}</span>,
        },
        {
          title: t('recruit.personalParticular.crHistory.approvalResult'),
          dataIndex: 'action',
          hideInSearch: true,
          render: (_: any, data: any) => {
            return t(`recruit.personalParticular.crHistory.approvalResult.options.${data.action}`);
          },
        },
        {
          title: t('recruit.personalParticular.crHistory.approvalDateTime'),
          dataIndex: 'actionDate',
          hideInSearch: true,
          render: (value: any) => <span>{formatDate(value)}</span>,
        },
      ],
      [intl],
    );

    return (
      <Dialog open={popUpVisible} onClose={() => setPopUpVisible(false)} fullScreen>
        <>
          <div style={{ display: 'flex', marginTop: 20 }}>
            <Button onClick={() => setPopUpVisible(false)} style={{ marginLeft: 20 }} variant="contained">
              {t('global.back.btnText')}
            </Button>
          </div>
          <div style={{ padding: 20 }}>
            <ProTable
              filterResetButtonText={t('recruit.button.reset')}
              filterSearchButtonText={t('recruit.button.search')}
              actionRef={actionRef}
              loading={isLoading}
              singleSelect={false}
              hideToolBar={true}
              hideAllSelect={false}
              rowKey="key"
              filterTitle={t('recruit.personalParticular.cr.changeRequestHistory')}
              headerTitle={''}
              columns={columns}
              request={(params, sorter, filter) => {
                return formatData(params);
              }}
              toolBarRender={() => []}
              pageSize={20}
              titleFontStyle={{ whiteSpace: 'nowrap' }}
            />
          </div>
        </>
      </Dialog>
    );
  },
);
